import React, { useEffect, useState } from "react";
import { mascotUrl, fetchMascotInfo } from "@/lib/api";

/**
 * CREEP Mascot — mystical black cat with purple aura.
 * Uses generated PNG when available; falls back to a stylized SVG.
 */
export const Mascot = ({ variant = "hero", size = 160, className = "", label }) => {
  const [available, setAvailable] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchMascotInfo()
      .then((d) => mounted && setAvailable(d.variants || []))
      .catch(() => {});
    return () => (mounted = false);
  }, []);

  const chosen = available.includes(variant) ? variant : available[0];
  const showImg = Boolean(chosen);

  return (
    <div
      data-testid={`mascot-${variant}`}
      className={`relative inline-flex items-center justify-center creep-float ${className}`}
      style={{ width: size, height: size }}
    >
      {/* Radial aura */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(124,58,237,0.55), rgba(76,29,149,0.35) 40%, transparent 70%)",
          filter: "blur(14px)",
        }}
      />
      {/* Inner container */}
      <div className={`relative creep-aura rounded-full overflow-hidden`} style={{ width: size * 0.86, height: size * 0.86 }}>
        {showImg ? (
          <img
            src={mascotUrl(chosen)}
            alt="FLAM mascot"
            onLoad={() => setLoaded(true)}
            className={`w-full h-full object-cover rounded-full transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
            data-testid={`mascot-img-${chosen}`}
          />
        ) : (
          <SVGFallback />
        )}
        {!loaded && showImg && <SVGFallback />}
      </div>
      {/* Orbital dots */}
      <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-violet-400 creep-glow-strong" />
      <span className="absolute top-1/2 -right-1 -translate-y-1/2 w-1 h-1 rounded-full bg-violet-300 creep-glow" />
      {label && (
        <span className="absolute -bottom-6 text-xs uppercase tracking-[0.25em] text-violet-300/80">
          {label}
        </span>
      )}
    </div>
  );
};

const SVGFallback = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <defs>
      <radialGradient id="body" cx="50%" cy="45%" r="55%">
        <stop offset="0%" stopColor="#1a1a20" />
        <stop offset="100%" stopColor="#050508" />
      </radialGradient>
      <radialGradient id="eye" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#c4b5fd" />
        <stop offset="60%" stopColor="#7C3AED" />
        <stop offset="100%" stopColor="#4C1D95" />
      </radialGradient>
    </defs>
    <circle cx="100" cy="100" r="98" fill="#09090B" />
    {/* Head */}
    <path
      d="M50 90 C50 62, 72 44, 100 44 C128 44, 150 62, 150 90 L150 130 C150 152, 128 168, 100 168 C72 168, 50 152, 50 130 Z"
      fill="url(#body)"
    />
    {/* Ears */}
    <path d="M52 78 L38 40 L74 62 Z" fill="#0a0a10" />
    <path d="M148 78 L162 40 L126 62 Z" fill="#0a0a10" />
    <path d="M54 74 L48 52 L68 62 Z" fill="#4C1D95" opacity="0.6" />
    <path d="M146 74 L152 52 L132 62 Z" fill="#4C1D95" opacity="0.6" />
    {/* Eyes */}
    <ellipse cx="80" cy="102" rx="10" ry="13" fill="url(#eye)" />
    <ellipse cx="120" cy="102" rx="10" ry="13" fill="url(#eye)" />
    <ellipse cx="80" cy="98" rx="3" ry="6" fill="#ffffff" opacity="0.85" />
    <ellipse cx="120" cy="98" rx="3" ry="6" fill="#ffffff" opacity="0.85" />
    {/* Nose & mouth */}
    <path d="M96 122 L104 122 L100 128 Z" fill="#7C3AED" />
    <path d="M100 128 Q92 138 86 132" stroke="#4C1D95" strokeWidth="2" fill="none" />
    <path d="M100 128 Q108 138 114 132" stroke="#4C1D95" strokeWidth="2" fill="none" />
    {/* Whiskers */}
    <g stroke="#5b21b6" strokeWidth="1.2" opacity="0.6">
      <line x1="52" y1="122" x2="82" y2="126" />
      <line x1="52" y1="132" x2="82" y2="130" />
      <line x1="148" y1="122" x2="118" y2="126" />
      <line x1="148" y1="132" x2="118" y2="130" />
    </g>
  </svg>
);

export default Mascot;
